import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Output,
  computed,
  input
} from '@angular/core';
import {
  TabViewHeaderSize,
  type ITabPanelHeader
} from '@design-system/components/m-tab-view/m-tab-view.component.types';
import { DesignSystemModule } from '@design-system/design-system.module';
import {
  EmptyStateSize,
  EmptyStateType
} from '@design-system/components/m-empty-states';
import type { ConversationStarterDTO as ConversationStarter } from '@libs/api';
import { PortalSparkleClusterComponent } from '@portal/app/shared/components/ai-assistant-panel/shared/sparkle-cluster.component';
import { PortalStarterChipComponent } from '@portal/app/shared/components/ai-assistant-panel/shared/starter-chip.component';

/**
 * Welcome screen + tabbed starter list. Shown before any agent has
 * been invoked. Pure presentation — emits the chosen starter on
 * click; the parent decides whether to invoke streaming chat or the
 * legacy summary path.
 *
 * `panel-starter-tabview` is a marker class for the panel SCSS to
 * reach `<p-tabview>` internals via `::ng-deep` (PrimeNG component
 * children can't be styled from inline classes alone).
 */
@Component({
  selector: 'portal-prompts-view',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DesignSystemModule,
    PortalSparkleClusterComponent,
    PortalStarterChipComponent
  ],
  templateUrl: './prompts-view.component.html'
})
export class PortalPromptsViewComponent {
  @HostBinding('class') readonly hostClass = 'contents';
  readonly userDisplayName = input<string | null>(null);
  readonly myStarters = input<readonly ConversationStarter[]>([]);
  readonly sharedStarters = input<readonly ConversationStarter[]>([]);
  readonly loading = input(false);
  readonly responding = input(false);
  /** m-tab-view headers — built by the parent so live counts stay accurate. */
  readonly tabHeaders = input.required<ITabPanelHeader[]>();

  @Output() readonly tabChange = new EventEmitter<number>();
  @Output() readonly askStarter = new EventEmitter<ConversationStarter>();

  protected readonly tabHeaderSize = TabViewHeaderSize.small;
  protected readonly EmptyStateType = EmptyStateType;
  protected readonly EmptyStateSize = EmptyStateSize;

  protected readonly welcomeTitle = computed(() => {
    const name = this.userDisplayName();
    return name ? `Welcome, ${name}!` : 'Welcome!';
  });

  protected readonly hasPrompts = computed(
    () => this.myStarters().length > 0 || this.sharedStarters().length > 0
  );
}
